import { BRAND } from '../Data/brand';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import ReportMetricCard from './ReportMetricCard';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const csvHeaders = [
  { label: 'Date', key: 'inv_date' },
  { label: 'Item Name', key: 'item_name' },
  { label: 'Payment Method', key: 'paymentMethod' },
  { label: 'Sold Quantity', key: 'sold' },
  { label: 'Line Gross Sales', key: 'gross_sales' },
];

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '0.00';
  return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(today),
  };
};

const ProductWiseSales = () => {
  const monthBounds = useMemo(() => getMonthBounds(), []);
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [itemName, setItemName] = useState('');
  const [fromDate, setFromDate] = useState(monthBounds.start);
  const [toDate, setToDate] = useState(monthBounds.end);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const fetchSalesData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get('https://serverkf.ccsoftwares.com/sales/productWise');
        const data = (response.data || []).map((record) => ({
          ...record,
          paymentMethod: record.paymentMethod || 'Unknown',
        }));
        data.sort((a, b) => {
          const dateA = new Date(a.inv_date);
          const dateB = new Date(b.inv_date);
          return dateB - dateA;
        });
        setSalesData(data);
      } catch (err) {
        console.error('Failed to fetch product-wise sales', err);
        setError('Unable to load product sales data at the moment.');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesData();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [itemName, fromDate, toDate, paymentMethod]);

  const paymentMethodOptions = useMemo(
    () => [...new Set(salesData.map((record) => record.paymentMethod).filter(Boolean))].sort(),
    [salesData]
  );

  const filteredData = useMemo(() => {
    if (!salesData.length) return [];

    const normalizedItem = itemName.trim().toLowerCase();
    const from = fromDate ? new Date(fromDate) : null;
    if (from) {
      from.setHours(0, 0, 0, 0);
    }
    const to = toDate ? new Date(toDate) : null;
    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    return salesData.filter((record) => {
      const matchesItem = normalizedItem
        ? record.item_name?.toLowerCase().includes(normalizedItem)
        : true;

      if (!matchesItem) {
        return false;
      }

      if (paymentMethod && record.paymentMethod !== paymentMethod) {
        return false;
      }

      if (!from && !to) {
        return true;
      }

      const recordDate = record.inv_date ? new Date(record.inv_date) : null;
      if (!recordDate) {
        return false;
      }

      const afterFrom = from ? recordDate >= from : true;
      const beforeTo = to ? recordDate <= to : true;
      return afterFrom && beforeTo;
    });
  }, [salesData, itemName, fromDate, toDate, paymentMethod]);

  const paginatedData = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredData;
    }
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const todaysSales = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return filteredData.reduce((accumulator, record) => {
      const recordDate = record.inv_date ? new Date(record.inv_date) : null;
      if (!recordDate) return accumulator;
      recordDate.setHours(0, 0, 0, 0);
      if (recordDate.getTime() === today.getTime()) {
        return accumulator + Number(record.sold || 0);
      }
      return accumulator;
    }, 0);
  }, [filteredData]);

  const selectedPeriodQuantity = useMemo(
    () =>
      filteredData.reduce(
        (accumulator, record) => accumulator + Number(record.sold || 0),
        0
      ),
    [filteredData]
  );

  const selectedPeriodGrossSales = useMemo(
    () =>
      filteredData.reduce(
        (accumulator, record) => accumulator + Number(record.gross_sales || 0),
        0
      ),
    [filteredData]
  );

  const uniqueProductsCount = useMemo(() => {
    const unique = new Set(filteredData.map((record) => record.item_name));
    return unique.size;
  }, [filteredData]);

  const csvData = useMemo(() => filteredData, [filteredData]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Chart data
  const groupedByDate = useMemo(() => {
    const grouping = {};
    filteredData.forEach(({ inv_date, item_name: name, sold }) => {
      if (!grouping[inv_date]) {
        grouping[inv_date] = {};
      }
      grouping[inv_date][name] = (grouping[inv_date][name] || 0) + Number(sold || 0);
    });
    return grouping;
  }, [filteredData]);

  const uniqueProducts = useMemo(
    () => [...new Set(filteredData.map((record) => record.item_name))],
    [filteredData]
  );

  const clusteredChartData = useMemo(
    () =>
      Object.keys(groupedByDate).map((date) => {
        const row = { date };
        uniqueProducts.forEach((product) => {
          row[product] = groupedByDate[date][product] || 0;
        });
        return row;
      }),
    [groupedByDate, uniqueProducts]
  );

  const colours = BRAND.colors.chart;

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Product Wise Sales Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Evaluate item performance with date and product filters.
          </Typography>
        </Box>
        <CSVLink
          data={csvData}
          headers={csvHeaders}
          filename="product-wise-sales.csv"
          style={{ textDecoration: 'none' }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
            disabled={!filteredData.length}
          >
            Download CSV
          </Button>
        </CSVLink>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <ReportMetricCard
            label="Today's Sold Quantity"
            value={todaysSales.toLocaleString()}
            tooltip="Quantity total from invoice detail rows for today. This is a unit quantity, not a gross or net sales amount."
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <ReportMetricCard
            label="Selected Period Sold Quantity"
            value={selectedPeriodQuantity.toLocaleString()}
            tooltip="Quantity total from filtered invoice detail rows. This is a unit quantity, not a gross or net sales amount."
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <ReportMetricCard
            label="Selected Line Gross Sales"
            value={`Rs. ${formatCurrency(selectedPeriodGrossSales)}`}
            tooltip="Gross sales from invoice detail totals for the filtered products. This is before invoice-level discount and service charge, so it is not net sales."
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <ReportMetricCard
            label="Unique Products (Filtered)"
            value={uniqueProductsCount.toLocaleString()}
            tooltip="Count of unique products in the filtered result. This is not a gross or net sales amount."
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              label="Search Item Name"
              placeholder="Start typing a product name"
              fullWidth
              value={itemName}
              onChange={(event) => setItemName(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Payment Method"
              fullWidth
              value={paymentMethod}
              onChange={(event) => setPaymentMethod(event.target.value)}
            >
              <MenuItem value="">All Payment Methods</MenuItem>
              {paymentMethodOptions.map((method) => (
                <MenuItem key={method} value={method}>
                  {method}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: 'var(--brand-primary)' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Item Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Payment Method</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Sold Quantity</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Line Gross Sales (Rs.)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No product sales match your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => (
                  <TableRow key={`${row.inv_date}-${row.item_name}-${row.paymentMethod}-${index}`}>
                    <TableCell>{row.inv_date}</TableCell>
                    <TableCell>{row.item_name}</TableCell>
                    <TableCell>{row.paymentMethod}</TableCell>
                    <TableCell>{Number(row.sold || 0).toLocaleString()}</TableCell>
                    <TableCell>{formatCurrency(row.gross_sales)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && !error && (
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
          sx={{ mt: 2 }}
        />
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Product Contribution by Date
        </Typography>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={clusteredChartData}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              {uniqueProducts.map((product, index) => (
                <Bar key={product} dataKey={product} fill={colours[index % colours.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
    </Box>
  );
};

export default ProductWiseSales;
